/**
 * Which sentence a successful write is acknowledged with.
 *
 * The API client raises a toast after every write (POST, PUT, PATCH, DELETE) that succeeds, so a
 * person always hears that what they did took. Most writes are named here, most specific first; any
 * write not named falls back to a generic "Saved" / "Removed", so a new route is acknowledged from
 * the day it ships and is only worded better later. `null` means say nothing: the write is the
 * session itself (sign-in, sign-out) or a background keep-alive, and a toast would be noise.
 *
 * Strings live in `toast.*` in the language pack (D47), never here.
 */
type Rule = readonly [method: string, path: RegExp, key: string | null];

const RULES: readonly Rule[] = [
  // The session itself.
  ['POST', /^\/auth\/(logout|login|refresh|device\/resend)/, null],
  ['POST', /^\/auth\/change-password/, 'toast.passwordChanged'],
  ['POST', /^\/auth\/(forgot|reset)/, null],

  // Business verification (KYC).
  ['POST', /^\/shop\/kyc-documents/, 'toast.kycFiled'],
  ['DELETE', /^\/shop\/kyc-documents/, 'toast.kycRemoved'],
  ['POST', /^\/retailers\/[^/]+\/kyc\/approve/, 'toast.kycApproved'],
  ['POST', /^\/retailers\/[^/]+\/kyc\/reject/, 'toast.kycRejected'],

  // Cart and ordering.
  ['PUT', /^\/shop\/cart\/lines/, 'toast.cartSaved'],
  ['DELETE', /^\/shop\/cart/, 'toast.cartCleared'],
  ['POST', /^\/shop\/orders\/[^/]+\/reorder/, 'toast.reordered'],
  ['POST', /^\/shop\/orders\/[^/]+\/decline/, 'toast.orderDeclined'],
  ['POST', /^\/shop\/orders\/[^/]+\/request-revision/, 'toast.revisionRequested'],
  ['POST', /^\/shop\/orders\/[^/]+\/(messages|queries)/, 'toast.messageSent'],
  ['POST', /^\/shop\/orders\/[^/]+\/change-requests/, 'toast.changeRequested'],
  ['POST', /^\/shop\/orders\/[^/]+\/payments/, 'toast.proofSent'],
  ['POST', /^\/shop\/orders\/[^/]+\/(submit|confirm|cancel|accept)/, 'toast.orderUpdated'],
  ['POST', /^\/orders\/[^/]+\/transition/, 'toast.orderUpdated'],
  ['POST', /^\/orders\/bulk-status/, 'toast.ordersUpdated'],
  ['POST', /^\/orders\/[^/]+\/(messages|queries)/, 'toast.messageSent'],

  // Manufacturing and shipping.
  ['POST', /^\/manufacturing\/orders\/[^/]+\/acknowledge/, 'toast.acknowledged'],
  ['POST', /^\/manufacturing\/orders\/[^/]+\/dispatch-date/, 'toast.dispatchDateSaved'],
  ['POST', /^\/consignment-invoices\/[^/]+\/cancel/, 'toast.consignmentInvoiceCancelled'],
  ['POST', /^\/consignment-invoices$/, 'toast.consignmentInvoiceRaised'],
  ['POST', /^\/consignment-invoices\/[^/]+\/shipment/, 'toast.shipmentCreated'],
  ['PUT', /^\/consignment-invoices\/[^/]+\/lines/, 'toast.saved'],
  ['POST', /^\/shop\/shipments\/[^/]+\/receipt/, 'toast.receiptConfirmed'],

  // Sales and payables.
  ['POST', /^\/shop\/inventory\/sold/, 'toast.salesReported'],
  ['POST', /^\/payables\/retailers\/[^/]+\/invoices/, 'toast.invoiceRecorded'],
  ['POST', /^\/payables\/retailers\/[^/]+\/payments/, 'toast.paymentRecorded'],
  ['POST', /^\/shop\/payables\/invoices\/[^/]+\/payments/, 'toast.proofSent'],
  ['POST', /^\/payables\/pieces\/[^/]+\/reverse-sale/, 'toast.saleReversed'],
  ['POST', /^\/payments\/[^/]+\/(approve|confirm)/, 'toast.paymentConfirmed'],
  ['POST', /^\/payments\/[^/]+\/reject/, 'toast.paymentRejected'],

  // Returns, losses and stock.
  ['POST', /^\/shop\/returns\/[^/]+\/withdraw/, 'toast.returnWithdrawn'],
  ['POST', /^\/shop\/(returns|losses)/, 'toast.submitted'],
  ['POST', /^\/returns\/recalls$/, 'toast.recallRaised'],
  ['POST', /^\/stock\//, 'toast.saved'],

  // Retailers and people.
  ['POST', /^\/retailers\/[^/]+\/status/, 'toast.statusChanged'],
  ['POST', /^\/retailers\/[^/]+\/terms/, 'toast.termsSaved'],
  ['PUT', /^\/retailers\/[^/]+\/budget/, 'toast.budgetSaved'],
  ['POST', /^\/applications\/[^/]+\/(approve|reject|request-information)/, 'toast.decisionRecorded'],
  ['POST', /^\/(users|retailer-users|shop\/team)$/, 'toast.userAdded'],
  ['POST', /^\/roles$/, 'toast.roleCreated'],
  ['DELETE', /^\/roles\//, 'toast.roleDeleted'],
];

/** The language-pack key to acknowledge this write with, or null to stay quiet. */
export function successKey(method: string, path: string): string | null {
  const m = method.toUpperCase();
  if (m === 'GET' || m === 'HEAD') return null;
  const bare = path.split('?')[0] ?? path;
  for (const [rm, re, key] of RULES) {
    if (rm === m && re.test(bare)) return key;
  }
  return m === 'DELETE' ? 'toast.removed' : 'toast.saved';
}
